import { Translate, useModalContentToggler } from '@components'

import {
  Color,
  Flex,
  Icon,
  InternalAccessor,
  Spacing,
  TableCellContent,
  TableColumn,
  TableHoverActions,
  Text,
} from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerModalState } from '../../../../../../../enums/CustomerModalState'
import { CustomerType } from '../../../../../../../enums/customerType'
import { useCustomerForm } from '../../../../../contexts/customerFormContext'
import { CompanyContactPersonsTableAccessor } from '../../../../../enums/companyContactPersonsTableAccessor'
import { useContactPersonFormActions } from '../../../../../hooks/useContactPersonFormActions'
import { CompanyContactPersonsTableRow } from '../../../../../types/companyContactPersonsTableRow'
import * as Styled from '../styles'

export const useContactPersonsTableColumns = (): TableColumn<CompanyContactPersonsTableRow>[] => {
  const { t } = useTranslation()
  const { contactPersons, customerForm, customerType, setContactPersonIdToEdit } = useCustomerForm()
  const { setValue } = customerForm
  const { clearMainPrimaryContactFields } = useContactPersonFormActions(customerForm)
  const { setActiveState } = useModalContentToggler()

  const handleDeleteContactPerson = useCallback(
    (contactToDelete: CompanyContactPersonsTableRow) => {
      if (!contactToDelete) {
        return
      }

      const isPrimaryContact = contactToDelete[CompanyContactPersonsTableAccessor.IsPrimary]

      const updatedContacts = contactPersons.filter((contact) => contact.id !== contactToDelete.id)

      if (isPrimaryContact && updatedContacts.length >= 0) {
        clearMainPrimaryContactFields()
      }

      setValue('contactPersons', updatedContacts, { shouldDirty: true })
    },
    [clearMainPrimaryContactFields, contactPersons, setValue],
  )

  const handleUpdateContactPerson = useCallback(
    (contactPersonToEdit: CompanyContactPersonsTableRow) => {
      if (!contactPersonToEdit) {
        return
      }

      setContactPersonIdToEdit(contactPersonToEdit.id)
      setValue('contactFirstName', contactPersonToEdit.firstName, { shouldDirty: true })
      setValue('contactLastName', contactPersonToEdit.lastName, { shouldDirty: true })
      setValue('contactEmail', contactPersonToEdit.email, { shouldDirty: true })
      setValue('isContactPrimary', contactPersonToEdit.isPrimary, { shouldDirty: true })

      setActiveState(CustomerModalState.EditContactPerson)
    },
    [setActiveState, setContactPersonIdToEdit, setValue],
  )

  const columns: TableColumn<CompanyContactPersonsTableRow>[] = useMemo(
    () => [
      {
        accessor: CompanyContactPersonsTableAccessor.FullName,
        Header: <Translate value="customer_modal.company_contacts.full_name" />,
        size: 'xs',
        truncate: true,
        Cell: ({ row, value }) => {
          if (customerType !== CustomerType.Company) {
            return <Text>{value}</Text>
          }

          const { isPrimary } = row.original

          return (
            <Flex alignItems="center" gap={Spacing.S8}>
              <Text>{value}</Text>
              {isPrimary && (
                <Styled.StarIconContainer>
                  <Icon icon="star" size="sm" color={Color.White} />
                </Styled.StarIconContainer>
              )}
            </Flex>
          )
        },
      },
      {
        accessor: CompanyContactPersonsTableAccessor.EmailAddress,
        Header: <Translate value="customer_modal.company_contacts.email" />,
        size: 'xs',
        truncate: true,
        Cell: ({ value }) => (
          <TableCellContent title={value} truncate>
            {value}
          </TableCellContent>
        ),
      },
      {
        accessor: InternalAccessor.HoverActions,
        Header: '',
        Cell: ({ row }) => (
          <TableHoverActions>
            <TableHoverActions.Item
              icon="squarePen"
              label={t('edit')}
              onClick={() => handleUpdateContactPerson(row.original)}
            />
            <TableHoverActions.Item
              icon="trash"
              label={t('delete')}
              onClick={() => handleDeleteContactPerson(row.original)}
            />
          </TableHoverActions>
        ),
      },
    ],
    [customerType, handleDeleteContactPerson, handleUpdateContactPerson, t],
  )

  return columns
}
